import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { FastAverageColor } from "fast-average-color";
import { AnimatePresence } from "framer-motion";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Widget.css";
import "./App.css";

// Components
import LoadingSpinner from "./components/LoadingSpinner";
import UserProfile from "./components/UserProfile";
import NothingPlayingCard from "./components/NothingPlayingCard";
import NowPlayingCard from "./components/NowPlayingCard";
import TabNavigation from "./components/TabNavigation";
import TopTracksTab from "./components/TopTracksTab";
import TopArtistsTab from "./components/TopArtistsTab";
import RecentlyPlayedTab from "./components/RecentlyPlayedTab";
import PlaylistsTab from "./components/PlaylistsTab";
import SavedAlbumsTab from "./components/SavedAlbumsTab";
import SavedShowsTab from "./components/SavedShowsTab";
import NextInQueue from "./components/NextInQueue";
import NowPlayingButton from "./components/NowPlayingButton";
import MoreInfoButton from "./components/MoreInfoButton";
import VercelAddOns from "./components/VercelAddOns";
import { SpotifyWrapped } from "./components/SpotifyWrapped/SpotifyWrapped";
import { cache, CACHE_KEYS, CACHE_DURATIONS } from "./utils/cache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_BAR_LIGHTNESS = 0.6;

// Lift dark colors so the progress bar stays visible on the dark track
const ensureVisible = ([r, g, b]) => {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let l = (max + min) / 2;
  if (l >= MIN_BAR_LIGHTNESS) return [r, g, b];

  const d = max - min;
  let h = 0, s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  l = MIN_BAR_LIGHTNESS;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [r1, g1, b1].map((v) => Math.round((v + m) * 255));
};

function App() {
  const [track, setTrack] = useState(null);
  const [user, setUser] = useState(null);
  const [topTracks, setTopTracks] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [savedAlbums, setSavedAlbums] = useState([]);
  const [savedShows, setSavedShows] = useState([]);
  const [nextInQueue, setNextInQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gradient, setGradient] = useState(
    "linear-gradient(90deg, #1DB954, #1ed760)"
  );
  const [activeTab, setActiveTab] = useState("tracks");
  const [showNothingPlaying, setShowNothingPlaying] = useState(false);
  const [showWrapped, setShowWrapped] = useState(false);
  const [wrappedData, setWrappedData] = useState(null);
  const [wrappedAvailable, setWrappedAvailable] = useState(false);

  const fac = useMemo(() => new FastAverageColor(), []);

  // Function to create rounded favicon
  const createRoundedFavicon = useCallback((imageUrl) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      // Draw circle clip path
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Draw image
      ctx.drawImage(img, 0, 0, size, size);

      // Update favicon
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL();
    };
    img.src = imageUrl;
  }, []);

  const extractColors = useCallback(
    async (url) => {
      try {
        const color = await fac.getColorAsync(url, { algorithm: "dominant" });
        const [r, g, b] = ensureVisible(color.value);
        return `linear-gradient(90deg, rgb(${r}, ${g}, ${b}), rgba(${r}, ${g}, ${b}, 0.8))`;
      } catch (err) {
        console.error("Color extraction error:", err);
        return "linear-gradient(90deg, #1DB954, #1ed760)";
      }
    },
    [fac]
  );

  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/currently-playing-verbose`);
        const trackData = res.data;
        setTrack(trackData);

        if (trackData?.image_url) {
          const grad = await extractColors(trackData.image_url);
          setGradient(grad);
        }
      } catch {
        setTrack(null);
      }
    };

    const fetchUser = async () => {
      const cached = cache.get(CACHE_KEYS.USER);
      if (cached) {
        setUser(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/user-info`);
        setUser(res.data);
        cache.set(CACHE_KEYS.USER, res.data, CACHE_DURATIONS.user);
      } catch {
        setUser(null);
      }
    };

    const fetchTopTracks = async () => {
      const cached = cache.get(CACHE_KEYS.TOP_TRACKS);
      if (cached) {
        setTopTracks(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/top-five`);
        const tracks = res.data.top_tracks || [];
        setTopTracks(tracks);
        cache.set(CACHE_KEYS.TOP_TRACKS, tracks, CACHE_DURATIONS.topTracks);
      } catch {
        setTopTracks([]);
      }
    };

    const fetchTopArtists = async () => {
      const cached = cache.get(CACHE_KEYS.TOP_ARTISTS);
      if (cached) {
        setTopArtists(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/top-five-artists`);
        const artists = res.data || [];
        setTopArtists(artists);
        cache.set(CACHE_KEYS.TOP_ARTISTS, artists, CACHE_DURATIONS.topArtists);
      } catch {
        setTopArtists([]);
      }
    };

    const fetchRecentlyPlayed = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/recently-played?limit=5`);
        setRecentlyPlayed(res.data || []);
      } catch {
        setRecentlyPlayed([]);
      }
    };

    const fetchPlaylists = async () => {
      const cached = cache.get(CACHE_KEYS.PLAYLISTS);
      if (cached) {
        setPlaylists(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/my-playlists?limit=5`);
        const playlists = res.data || [];
        setPlaylists(playlists);
        cache.set(CACHE_KEYS.PLAYLISTS, playlists, CACHE_DURATIONS.playlists);
      } catch {
        setPlaylists([]);
      }
    };

    const fetchSavedAlbums = async () => {
      const cached = cache.get(CACHE_KEYS.SAVED_ALBUMS);
      if (cached) {
        setSavedAlbums(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/saved-albums?limit=20`);
        const albums = res.data || [];
        setSavedAlbums(albums);
        cache.set(CACHE_KEYS.SAVED_ALBUMS, albums, CACHE_DURATIONS.savedMedia);
      } catch {
        setSavedAlbums([]);
      }
    };

    const fetchSavedShows = async () => {
      const cached = cache.get(CACHE_KEYS.SAVED_SHOWS);
      if (cached) {
        setSavedShows(cached);
        return;
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/saved-shows?limit=20`);
        const shows = res.data || [];
        setSavedShows(shows);
        cache.set(CACHE_KEYS.SAVED_SHOWS, shows, CACHE_DURATIONS.savedMedia);
      } catch {
        setSavedShows([]);
      }
    };

    const fetchNextInQueue = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/next-in-queue`);
        setNextInQueue(res.data || null);
      } catch {
        setNextInQueue(null);
      }
    };

    // Test if Spotify Wrapped API is available
    const testWrappedAPI = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/wrapped?period=short_term&limit=1`);
        // If we get a successful response with data, show the button
        if (response.data && (response.data.top_artists || response.data.top_tracks || response.data.top_genres)) {
          setWrappedAvailable(true);
        }
      } catch (error) {
        console.log("Wrapped API not available:", error.message);
        setWrappedAvailable(false);
      }
    };

    const init = async () => {
      await Promise.all([
        fetchUser(),
        fetchTrack(),
        fetchTopTracks(),
        fetchTopArtists(),
        fetchRecentlyPlayed(),
        fetchPlaylists(),
        fetchSavedAlbums(),
        fetchSavedShows(),
        fetchNextInQueue(),
        testWrappedAPI(), // Add this test
      ]);
      setLoading(false);
    };
    init();

    const interval = setInterval(() => {
      fetchTrack();
      fetchRecentlyPlayed();
      fetchNextInQueue();
    }, 2000);
    return () => clearInterval(interval);
  }, [extractColors]);

  // Update document title with username
  useEffect(() => {
    if (user?.display_name) {
      document.title = `${user.display_name}'s Spotify`;
    } else {
      document.title = "Spotify Broadcast";
    }

    // Update favicon with rounded user profile picture
    if (user?.image) {
      createRoundedFavicon(user.image);
    }
  }, [user, createRoundedFavicon]);

  // Fetch Spotify Wrapped data
  const fetchWrappedData = async () => {
    try {
      const [wrappedRes, showsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/wrapped?period=long_term`),
        axios.get(`${BACKEND_URL}/saved-shows`)
      ]);
      
      setWrappedData({
        ...wrappedRes.data,
        saved_shows: showsRes.data || []
      });
    } catch (error) {
      console.error("Failed to fetch wrapped data:", error);
    }
  };

  // Show Spotify Wrapped view
  if (showWrapped) {
    return wrappedData ? (
      <SpotifyWrapped data={wrappedData} onClose={() => setShowWrapped(false)} />
    ) : (
      <LoadingSpinner />
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  // Force nothing playing view or actually nothing playing
  if (showNothingPlaying || !track || !track.track_id) {
    return (
      <>
        <div className="broadcast-page">
          <main className="container broadcast-container is-dashboard">
            <UserProfile user={user} isLive={Boolean(track?.track_id)} />

            {/* Remove the Spotify Wrapped Button from here */}

            {/* Toggle button - only show when music is actually playing */}
            {track && track.track_id && (
              <NowPlayingButton
                showNothingPlaying={showNothingPlaying}
                setShowNothingPlaying={setShowNothingPlaying}
              />
            )}

            <div className="row g-4 info-grid">
              {/* Info Tab with Tabs */}
              <div className="col-12 col-lg-12" id="info-tab">
               <div className="library-panel">
                {(!track || !track.track_id) && <NothingPlayingCard />}
                <TabNavigation
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onWrappedClick={async () => {
                    await fetchWrappedData();
                    setShowWrapped(true);
                  }}
                  showWrappedTab={wrappedAvailable}
                />

                <AnimatePresence mode="wait">
                  {activeTab === "tracks" && (
                    <TopTracksTab tracks={topTracks} />
                  )}
                  {activeTab === "artists" && (
                    <TopArtistsTab artists={topArtists} />
                  )}
                  {activeTab === "recent" && (
                    <RecentlyPlayedTab tracks={recentlyPlayed} />
                  )}
                  {activeTab === "playlists" && (
                    <PlaylistsTab playlists={playlists} />
                  )}
                  {activeTab === "albums" && (
                    <SavedAlbumsTab albums={savedAlbums} />
                  )}
                  {activeTab === "shows" && (
                    <SavedShowsTab shows={savedShows} />
                  )}
                </AnimatePresence>
               </div>
              </div>
            </div>
          </main>
        </div>
        <VercelAddOns />
      </>
    );
  }

  // Currently playing track view
  return (
    <>
      <div className="broadcast-page">
        <main className="container broadcast-container is-dashboard">
          <UserProfile user={user} maxNameLength={100} isLive />

          {/* Remove the Spotify Wrapped Button from here too */}

          {/* Toggle button */}
          <MoreInfoButton setShowNothingPlaying={setShowNothingPlaying} />

          <div className="row g-4 dashboard-grid">
            {/* Now Playing Tab */}
            <div className="col-12 col-lg-8">
              <NowPlayingCard track={track} gradient={gradient} />
            </div>

            {/* Info Tab */}
            <div className="col-12 col-lg-4" id="info-tab">
              <div className="sidebar-panel">
                <NextInQueue track={nextInQueue} />
                <div className="sidebar-recent">
                  <RecentlyPlayedTab tracks={recentlyPlayed} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      <VercelAddOns />
    </>
  );
}

export default App;
